import { FC, useEffect, useState } from "react";
import {
  Button,
  TextField,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Box,
  Autocomplete,
  Grid,
  Chip,
  FormHelperText,
  useMediaQuery,
} from "@mui/material";
import { Descendant } from "slate";
import RichTextEditor from "../../components/RichTextEditor/RichTextEditor";
import { useSelector } from "react-redux";
import { RootState } from "../../../store/store";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { VisuallyHiddenInput } from "./CreateContentUtils";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import { createContent, updateContent } from "../CMSActions";
import {
  IContent,
  ICreateContentPayload,
} from "../../../models/admin/cms/contentTypes";
import MessageDialog from "../../../components/MessageDialog/MessageDialog";

interface IContentFields {
  fieldLabel: string;
  fieldType: string;
  maxLength: string;
  required: boolean;
  value?: string;
}

interface IContentType {
  contentTypeName: string;
  fields: IContentFields[];
}
interface editContentId {
  editContentId: string;
}
const CreateContent: FC<editContentId> = ({ editContentId }) => {
  const dispatch = useAppDispatch();
  const isMobile = useMediaQuery("(max-width:767px)");

  const { contentTypes } = useSelector(
    (state: RootState) => state.contentManagement
  );
  const initialValue: Descendant[] = [
    {
      type: "paragraph",
      children: [{ text: "" }],
    },
  ];
  const [fields, setFields] = useState<IContentFields[]>([]);
  const [contentType, setContentType] = useState(
    contentTypes?.length > 0 ? contentTypes[0].contentTypeName : ""
  );
  const [effectiveStartDate, setEffectiveStartDate] = useState("");
  const [effectiveEndDate, setEffectiveEndDate] = useState("");
  const [tags, setTags] = useState<string[]>([]);
  const [value, setValue] = useState<Descendant[]>(initialValue);
  const [editorKey, setEditorKey] = useState(0);
  const [formValues, setFormValues] = useState<{
    [key: string]: string | File | null;
  }>({});
  const [inputValue, setInputValue] = useState<string>("");
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [messageDialogOpen, setMessageDialogOpen] = useState<boolean>(false);
  const [editContent, setEditContent] = useState<IContent>();

  const { content } = useAppSelector(
    (state: RootState) => state.contentManagement
  );

  const { isSuccess, calledReducerType, isError } = useAppSelector(
    (state) => state.common
  );

  const handleSubmit = (e: any) => {
    e.preventDefault();
    // Handle form submission logic
  };

  const getImageSrc = (image: string | File | undefined) => {
    if (!image) return "";

    if (typeof image === "string") {
      return image.startsWith("data:image") || image.startsWith("http")
        ? image
        : `data:image/png;base64,${image}`;
    }

    return URL.createObjectURL(image);
  };

  const handleFieldChange = (fieldLabel: string, value: any) => {
    setFormValues((prev) => ({
      ...prev,
      [fieldLabel]: value,
    }));
  };

  const handleTagsChange = (event: React.ChangeEvent<{}>, value: string[]) => {
    setTags(value);
  };

  const handleTagInputKeyDown = (
    event: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (event.key === "Enter" || event.key === ",") {
      event.preventDefault();
      const newTag = inputValue.trim();
      if (newTag && !tags.includes(newTag)) {
        setTags((prev) => [...prev, newTag]);
        setInputValue("");
      }
    }
  };

  const resetForm = () => {
    setFormValues({});
    setEffectiveStartDate("");
    setEffectiveEndDate("");
    setTags([]);
    setValue([{ type: "paragraph", children: [{ text: "" }] }]);
    setEditorKey((prev) => prev + 1);
    setErrors({});
    setInputValue("");
    setEditContent(undefined);
  };

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};
    // Get today's date in YYYY-MM-DD format
    const today = new Date().toISOString().split("T")[0];

    // Check if we are in edit mode
    const isEditMode = !!editContent;

    // Validate dates
    if (!isEditMode) {
      // In create mode, both dates are required
      if (!effectiveStartDate) {
        newErrors.effectiveStartDate = "Start date is required";
      } else if (effectiveStartDate && effectiveStartDate < today) {
        newErrors.effectiveStartDate = "Start date cannot be in the past";
      }
      if (!effectiveEndDate) {
        newErrors.effectiveEndDate = "End date is required";
      } else if (effectiveEndDate < effectiveStartDate) {
        newErrors.effectiveEndDate = "End date cannot be before start date";
      } else if (effectiveEndDate && effectiveEndDate < today) {
        newErrors.effectiveEndDate = "End date cannot be in the past";
      }
    } else {
      // In edit mode, check if the user has changed the dates
      if (!effectiveStartDate && !editContent?.effectiveStartDate) {
        newErrors.effectiveStartDate = "Start date is required";
      } else if (effectiveStartDate < today) {
        newErrors.effectiveStartDate = "Start date cannot be in the past";
      }

      if (!effectiveEndDate && !editContent?.effectiveEndDate) {
        newErrors.effectiveEndDate = "End date is required";
      } else if (effectiveEndDate < effectiveStartDate) {
        newErrors.effectiveEndDate = "End date cannot be before start date";
      } else if (effectiveEndDate < today) {
        newErrors.effectiveEndDate = "End date cannot be in the past";
      }
    }

    // Validate required fields
    fields.forEach((field) => {
      if (field.required) {
        const value = formValues[field.fieldLabel];
        if (!value) {
          newErrors[field.fieldLabel] = `${field.fieldLabel} is required`;
        }
        // Special check for image fields
        if (field.fieldType === "image" && (!value || value === null)) {
          newErrors[field.fieldLabel] = `${field.fieldLabel} is required`;
        }
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleSave = async (status: "Draft" | "Published") => {
    if (!validateForm()) return;

    if (contentType) {
      // Convert formValues into a structured payload
      const fieldsData: { [key: string]: string | File | null } = {};

      const convertFileToBase64 = (file: File): Promise<string> => {
        return new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result as string);
          reader.onerror = (error) => reject(error);
          reader.readAsDataURL(file);
        });
      };

      // Prepare fields data with base64 conversion for image fields
      await Promise.all(
        fields.map(async (field) => {
          const value = formValues[field.fieldLabel];

          if (field.fieldType === "image" && value instanceof File) {
            try {
              const base64Value = await convertFileToBase64(value);
              fieldsData["imageUrl"] = base64Value;
            } catch (error) {
              console.error("Error converting file to base64", error);
            }
          } else if (
            field.fieldType === "image" &&
            typeof value === "string" &&
            value?.startsWith("data:image")
          ) {
            fieldsData["imageUrl"] = value;
          } else {
            fieldsData[field.fieldLabel] = value;
          }
        })
      );

      // Prepare the payload based on whether we're editing or creating content
      const payload: ICreateContentPayload = {
        contentTypeName: editContent
          ? editContent?.contentTypeName
          : contentType,
        fields: fieldsData,
        effectiveStartDate:
          effectiveStartDate || editContent?.effectiveStartDate || "",
        effectiveEndDate:
          effectiveEndDate || editContent?.effectiveEndDate || "",
        tags,
        status,
      };

      if (editContent) {
        const result = await dispatch(
          updateContent({ ...payload, id: editContentId })
        );
        if (result.meta.requestStatus === "fulfilled") {
          resetForm();
        }
      } else {
        const result = await dispatch(createContent(payload));
        if (result.meta.requestStatus === "fulfilled") {
          resetForm();
        }
      }
    }
  };

  const renderField = (field: IContentFields) => {
    switch (field?.fieldType) {
      case "text":
        return (
          <FormControl fullWidth error={!!errors[field.fieldLabel]}>
            <TextField
              key={field?.fieldLabel}
              placeholder="Enter text"
              label={field?.fieldLabel}
              required={field?.required}
              InputLabelProps={{ shrink: true }}
              inputProps={{ maxLength: field?.maxLength || undefined }}
              fullWidth
              value={formValues[field.fieldLabel] || ""}
              onChange={(e) => {
                handleFieldChange(field.fieldLabel, e.target.value);
                setErrors((prev) => ({ ...prev, [field.fieldLabel]: "" }));
              }}
              error={!!errors[field.fieldLabel]}
              helperText={errors[field.fieldLabel]}
            />
          </FormControl>
        );
      case "color":
        return (
          <TextField
            key={field?.fieldLabel}
            type="color"
            label={field?.fieldLabel}
            required={field?.required}
            InputLabelProps={{ shrink: true }}
            fullWidth
            value={formValues[field.fieldLabel] || ""}
            onChange={(e) =>
              handleFieldChange(field.fieldLabel, e.target.value)
            }
          />
        );
      case "link":
        return (
          <TextField
            key={field?.fieldLabel}
            placeholder="Enter URL"
            label={field?.fieldLabel}
            required={field?.required}
            InputLabelProps={{ shrink: true }}
            inputProps={{ maxLength: field?.maxLength || undefined }}
            fullWidth
            value={formValues[field.fieldLabel] || ""}
            onChange={(e) => {
              handleFieldChange(field.fieldLabel, e.target.value);
              setErrors((prev) => ({ ...prev, [field.fieldLabel]: "" }));
            }}
            error={!!errors[field.fieldLabel]}
            helperText={errors[field.fieldLabel]}
          />
        );
      case "image":
        const isBase64 = (value: any) =>
          typeof value === "string" && value.startsWith("data:image/");

        return (
          <FormControl fullWidth error={!!errors[field.fieldLabel]}>
            <Box
              sx={{
                width: "100%",
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "space-between",
              }}
            >
              <Button
                key={field?.fieldLabel}
                component="label"
                role={undefined}
                variant="contained"
                tabIndex={-1}
                startIcon={<CloudUploadIcon />}
                sx={{ borderRadius: "4px" }}
              >
                {field?.fieldLabel}
                {field?.required && "*"}
                <VisuallyHiddenInput
                  type="file"
                  accept="image/*"
                  required={field?.required}
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      if (!file.type.startsWith("image/")) {
                        alert(
                          "Please upload a valid image file. Only images are allowed."
                        );
                        return;
                      }
                      handleFieldChange(field.fieldLabel, file);
                      setErrors((prev) => ({
                        ...prev,
                        [field.fieldLabel]: "",
                      }));
                    }
                  }}
                />
              </Button>
              {formValues[field.fieldLabel] instanceof File && (
                <Chip
                  label={(formValues[field.fieldLabel] as File).name}
                  onDelete={() => handleFieldChange(field.fieldLabel, null)}
                  sx={{ maxWidth: "300px" }}
                />
              )}

              {isBase64(formValues[field.fieldLabel]) && (
                <img
                  src={getImageSrc(field.value)}
                  alt="Article Banner"
                  style={{ width: "150px", maxHeight: "150px" }}
                />
              )}
            </Box>
            {errors[field.fieldLabel] && (
              <FormHelperText>{errors[field.fieldLabel]}</FormHelperText>
            )}
          </FormControl>
        );
      case "multiline":
        return (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              width: "100%",
            }}
          >
            {/* {field.fieldLabel && (
              <InputLabel
                shrink
                sx={{
                  transform: "none",
                  marginBottom: "0",
                  fontSize: "0.875rem",
                  color: !!errors[field.fieldLabel]
                    ? "#ff1744"
                    : "rgba(0, 0, 0, 0.6);",
                }}
              >
                {field.fieldLabel}
                {field.required && <span> *</span>}
              </InputLabel>
            )} */}
            <RichTextEditor
              value={
                editContent
                  ? Array.isArray(field.value)
                    ? field.value
                    : typeof field.value === "string"
                    ? [{ type: "paragraph", children: [{ text: field.value }] }]
                    : []
                  : value
              }
              key={editorKey}
              placeholder="Enter content here"
              fontSize="16px"
              onChange={(value) => {
                handleFieldChange(field.fieldLabel, value);
              }}
              isError={!!errors[field.fieldLabel]}
              label={field.fieldLabel}
              required={true}
            />
            {errors[field.fieldLabel] && (
              <FormHelperText sx={{ paddingLeft: 2, color: "#ff1744" }}>
                {errors[field.fieldLabel]}
              </FormHelperText>
            )}
          </Box>
        );
      default:
        return null;
    }
  };

  const handleContentType = (e: any) => {
    setContentType(e.target.value);
  };

  const handleMessageDialogClose = () => {
    setMessageDialogOpen(false);
    window.location.reload();
  };

  useEffect(() => {
    if (
      (isSuccess || isError) &&
      (calledReducerType === "cms/createContent" ||
        calledReducerType === "cms/updateContent")
    ) {
      setMessageDialogOpen(true);
    }
  }, [isSuccess, isError, calledReducerType, dispatch]);

  useEffect(() => {
    if (contentType) {
      const test = contentTypes?.filter(
        (type) => type?.contentTypeName === contentType
      );
      setFields(test[0]?.fields);
    }
  }, [contentType]);

  useEffect(() => {
    if (editContentId && content?.length && !editContent) {
      const filteredEditContent = content.find(
        (item) => item.id === editContentId
      );
      if (filteredEditContent) {
        const contentTypeData = contentTypes.find(
          (type) => type.contentTypeName === filteredEditContent.contentTypeName
        );

        if (contentTypeData) {
          const fieldsWithValues = contentTypeData.fields.map((field) => ({
            ...field,
            value:
              field.fieldLabel === "Article Banner"
                ? filteredEditContent.fields["imageUrl"]
                : filteredEditContent.fields[field.fieldLabel],
          }));

          const prepopulatedFormValues: {
            [key: string]: string | File | null;
          } = {};
          fieldsWithValues.forEach((field) => {
            prepopulatedFormValues[field.fieldLabel] = field.value || "";
          });

          setFields(fieldsWithValues);
          setFormValues(prepopulatedFormValues);
          setEditContent({ ...filteredEditContent, fields: fieldsWithValues });
          setTags(filteredEditContent.tags || []);
          setEffectiveStartDate(filteredEditContent.effectiveStartDate || "");
          setEffectiveEndDate(filteredEditContent.effectiveEndDate || "");
        }
      }
    }
  }, [editContentId, content, contentTypes, editContent]);

  return (
    <>
      <MessageDialog
        open={messageDialogOpen}
        handleClose={handleMessageDialogClose}
      />
      <Box
        sx={{
          marginTop: "20px",
          display: "flex",
          justifyContent: "center",
          maxWidth: isMobile ? "calc(100vw - 40px)" : "none",
        }}
        id="create_content_container"
        data-test-id="create_content_container"
        aria-label="Create Content Form"
      >
        <Box
          sx={{ width: "600px", flexDirection: "column", gap: "24px" }}
          id="create_content_form_container"
          data-test-id="create_content_form_container"
          aria-label="Content Form Container"
        >
          <Box>
            <form
              onSubmit={handleSubmit}
              style={{ display: "flex", flexDirection: "column", gap: "24px" }}
              id="create_content_form"
              data-test-id="create_content_form"
              aria-label="Content Form"
            >
              <FormControl fullWidth>
                <InputLabel>Content Type</InputLabel>
                <Select
                  label="Content Type"
                  value={
                    editContent ? editContent?.contentTypeName : contentType
                  }
                  onChange={handleContentType}
                  id="create_content_type_select"
                  data-test-id="create_content_type_select"
                  aria-label="Select Content Type"
                >
                  {contentTypes?.map((type: IContentType) => (
                    <MenuItem
                      key={type.contentTypeName}
                      value={type.contentTypeName}
                      id={`create_content_type_option_${type.contentTypeName}`}
                      data-test-id={`create_content_type_option_${type.contentTypeName}`}
                      aria-label={`Content Type: ${type.contentTypeName}`}
                    >
                      {type.contentTypeName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <FormControl fullWidth error={!!errors.effectiveStartDate}>
                    <TextField
                      label="Effective Start Date"
                      type="date"
                      value={effectiveStartDate}
                      onChange={(e) => {
                        setEffectiveStartDate(e.target.value);
                        setErrors((prev) => ({
                          ...prev,
                          effectiveStartDate: "",
                        }));
                      }}
                      InputLabelProps={{ shrink: true }}
                      fullWidth
                      required
                      error={!!errors.effectiveStartDate}
                      helperText={errors.effectiveStartDate}
                      // Setting min date to today
                      inputProps={{
                        min: new Date().toISOString().split("T")[0],
                      }}
                      id="create_content_start_date"
                      data-test-id="create_content_start_date"
                      aria-label="Effective Start Date"
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={6}>
                  <FormControl fullWidth error={!!errors.effectiveEndDate}>
                    <TextField
                      label="Effective End Date"
                      type="date"
                      value={effectiveEndDate}
                      onChange={(e) => {
                        setEffectiveEndDate(e.target.value);
                        setErrors((prev) => ({
                          ...prev,
                          effectiveEndDate: "",
                        }));
                      }}
                      InputLabelProps={{ shrink: true }}
                      fullWidth
                      required
                      error={!!errors.effectiveEndDate}
                      helperText={errors.effectiveEndDate}
                      // Setting min date to today
                      inputProps={{
                        min: new Date().toISOString().split("T")[0],
                      }}
                      id="create_content_end_date"
                      data-test-id="create_content_end_date"
                      aria-label="Effective End Date"
                    />
                  </FormControl>
                </Grid>
              </Grid>

              <Autocomplete
                multiple
                freeSolo
                options={[]}
                value={tags}
                onChange={handleTagsChange}
                inputValue={inputValue}
                onInputChange={(event, newValue) => {
                  setInputValue(newValue);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    label="Tags"
                    placeholder="Add tags"
                    onKeyDown={handleTagInputKeyDown}
                    id="create_content_tags"
                    data-test-id="create_content_tags"
                    aria-label="Content Tags"
                  />
                )}
                id="create_content_tags_autocomplete"
                data-test-id="create_content_tags_autocomplete"
                aria-label="Add Content Tags"
              />

              <Box
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "16px",
                }}
                id="create_content_fields"
                data-test-id="create_content_fields"
                aria-label="Content Fields"
              >
                {editContent
                  ? editContent.fields.map((field: IContentFields) => (
                      <Box
                        key={field.fieldLabel}
                        style={{
                          borderRadius: "8px",
                          display: "flex",
                        }}
                        id={`create_content_field_${field.fieldLabel}`}
                        data-test-id={`create_content_field_${field.fieldLabel}`}
                        aria-label={`Content Field: ${field.fieldLabel}`}
                      >
                        {renderField(field)}
                      </Box>
                    ))
                  : fields.map((field: IContentFields) => (
                      <Box
                        key={field.fieldLabel}
                        style={{
                          borderRadius: "8px",
                          display: "flex",
                        }}
                        id={`create_content_field_${field.fieldLabel}`}
                        data-test-id={`create_content_field_${field.fieldLabel}`}
                        aria-label={`Content Field: ${field.fieldLabel}`}
                      >
                        {renderField(field)}
                      </Box>
                    ))}
              </Box>

              <Box
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: "16px",
                }}
                id="create_content_actions"
                data-test-id="create_content_actions"
                aria-label="Content Actions"
              >
                <Button
                  variant="outlined"
                  onClick={() => handleSave("Draft")}
                  sx={{ borderRadius: "4px" }}
                  disabled={!contentType}
                  id="create_content_draft_button"
                  data-test-id="create_content_draft_button"
                  aria-label="Save as Draft"
                >
                  Save as Draft
                </Button>
                <Button
                  variant="contained"
                  onClick={() => handleSave("Published")}
                  sx={{ borderRadius: "4px" }}
                  disabled={!contentType}
                  id="create_content_publish_button"
                  data-test-id="create_content_publish_button"
                  aria-label="Publish Content"
                >
                  Publish
                </Button>
              </Box>
            </form>
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default CreateContent;
